/* The Aesthetic Cove — snow.
   Two kinds, sharing one set of crystal snowflakes (the six-armed designs of
   the Christmas intro):

   1. Snowfall. Soft Snow and a little champagne, with fine snow dust between,
      drifting down behind the content of every band that carries .snow (added
      when the "Gentle snowfall" theme setting is on) or [data-snowfall]. Each
      band draws only while it is on screen. The canvas goes into the band's
      [data-snowfall-layer] element when it has one, otherwise at its start.
      The band's own background decides the crystals: on the taupe band they
      are Soft Snow; on snow, ivory and cashmere they are Soft Snow with a
      taupe-light rim, so a flake still reads on a light wall, and some are
      a champagne sparkle. A band can say which with data-snowfall-on="light"
      or "dark" instead of being measured.

   2. Snow from the pointer. Move the mouse and flakes shake loose from it and
      fall, swaying, for a second or two: it snows wherever you look. The same
      rule: Soft Snow on the dark bands, rimmed snow and champagne on the
      light ones. A tap on a phone lets go a small flurry. One fixed canvas
      above the page, drawn only while flakes are falling.

   Nothing moves for visitors who prefer reduced motion; they keep the still
   CSS snow. The first time a band (or anything holding a fir-sprig garland)
   scrolls into view it gets .is-in, which lets the baubles drop in. */
(() => {
  const theme = window.theme || {};
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const hasIO = 'IntersectionObserver' in window;

  const hosts = Array.from(document.querySelectorAll('.snow, [data-snowfall]'));
  const garlandHosts = Array.from(document.querySelectorAll('.garland')).map((g) => g.parentElement);
  const watch = Array.from(new Set([...hosts, ...garlandHosts].filter(Boolean)));

  /* ---------- first sight ---------- */
  if (watch.length) {
    if (!hasIO || reduce) {
      watch.forEach((el) => el.classList.add('is-in'));
    } else {
      const seen = new IntersectionObserver(
        (entries) =>
          entries.forEach((e) => {
            if (!e.isIntersecting) return;
            e.target.classList.add('is-in');
            seen.unobserve(e.target);
          }),
        { threshold: 0.15 }
      );
      watch.forEach((el) => seen.observe(el));
    }
  }
  if (reduce) return;

  /* ---------- crystal sprites, built once ---------- */
  // Soft sprites lose nothing below 1.5x, and every canvas pixel costs a frame.
  const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
  const SMALL = window.matchMedia('(max-width: 749px)').matches;
  const TAU = Math.PI * 2;
  const ARMS = [
    [[0.1, 0, 1, 0], [0.3, 0, 0.43, 0.22], [0.3, 0, 0.43, -0.22], [0.52, 0, 0.63, 0.19], [0.52, 0, 0.63, -0.19], [0.72, 0, 0.79, 0.12], [0.72, 0, 0.79, -0.12]],
    [[0, 0, 0.86, 0], [0.56, 0, 0.66, 0.17], [0.56, 0, 0.66, -0.17], [0.86, 0, 0.93, 0.07], [0.93, 0.07, 1, 0], [1, 0, 0.93, -0.07], [0.93, -0.07, 0.86, 0]],
    [[0, 0, 1, 0], [0.6, 0, 0.8, 0.17], [0.6, 0, 0.8, -0.17], [0.3, 0, 0.38, 0.12], [0.3, 0, 0.38, -0.12]],
    [[0.08, 0, 1, 0], [0.22, 0, 0.36, 0.24], [0.22, 0, 0.36, -0.24], [0.38, 0, 0.5, 0.2], [0.38, 0, 0.5, -0.2], [0.54, 0, 0.64, 0.16], [0.54, 0, 0.64, -0.16], [0.7, 0, 0.77, 0.11], [0.7, 0, 0.77, -0.11], [0.84, 0, 0.88, 0.06], [0.84, 0, 0.88, -0.06]],
  ];
  const HEX = [0.1, 0.3, 0, 0];
  // A crystal in one colour with a soft glow of the same; given a rim colour
  // it is first drawn wider in that, so a pale flake keeps an edge on a pale wall.
  function crystal(size, design, colour, lw, glow = 0.22, rim = '') {
    const c = document.createElement('canvas');
    c.width = c.height = size;
    const g = c.getContext('2d');
    const R = size * 0.4;
    g.translate(size / 2, size / 2);
    g.lineCap = 'round';
    g.lineJoin = 'round';
    g.beginPath();
    for (let k = 0; k < 6; k++) {
      const a = (k * TAU) / 6 - Math.PI / 2;
      const ca = Math.cos(a);
      const sa = Math.sin(a);
      for (const [u1, v1, u2, v2] of ARMS[design]) {
        g.moveTo((u1 * ca - v1 * sa) * R, (u1 * sa + v1 * ca) * R);
        g.lineTo((u2 * ca - v2 * sa) * R, (u2 * sa + v2 * ca) * R);
      }
    }
    if (HEX[design]) {
      for (let k = 0; k <= 6; k++) {
        const a = (k * TAU) / 6 - Math.PI / 2;
        const x = Math.cos(a) * HEX[design] * R;
        const y = Math.sin(a) * HEX[design] * R;
        if (k) g.lineTo(x, y);
        else g.moveTo(x, y);
      }
    }
    if (rim) {
      g.strokeStyle = rim;
      g.lineWidth = Math.max(2.4, R * lw * 2.6);
      g.shadowColor = rim;
      g.shadowBlur = R * glow;
      g.stroke();
    }
    g.strokeStyle = colour;
    g.lineWidth = Math.max(1, R * lw);
    g.shadowColor = rim ? 'transparent' : colour;
    g.shadowBlur = rim ? 0 : R * glow;
    g.stroke();
    return c;
  }
  const PX = Math.round(48 * dpr);
  // The sixteen sprites are drawn the first time snow is needed (after load,
  // when the browser is idle), not while the page is still arriving.
  let SNOW;
  let CHAMPAGNE;
  let SNOW_RIMMED;
  let SPARKLE;
  const sprites = () => {
    if (SNOW) return;
    // On the taupe band: Soft Snow, and a little champagne.
    SNOW = [0, 1, 2, 3].map((d) => crystal(PX, d, 'rgba(250,248,243,0.95)', 0.08));
    CHAMPAGNE = [0, 1, 2, 3].map((d) => crystal(PX, d, '#D9BE8A', 0.085));
    // On snow, ivory and cashmere: Soft Snow with a taupe-light rim, and a deeper champagne sparkle.
    SNOW_RIMMED = [0, 1, 2, 3].map((d) => crystal(PX, d, '#FAF8F3', 0.075, 0.2, 'rgba(139,117,100,0.5)'));
    SPARKLE = [0, 1, 2, 3].map((d) => crystal(PX, d, '#B8955A', 0.09, 0.14));
  };

  /* ---------- is a band light or dark? ---------- */
  // From the band's own background (walking up through transparent wrappers),
  // unless it says so with data-snowfall-on="light|dark".
  const luminance = (rgb) => {
    const m = /rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)(?:[,\s/]+([\d.]+))?/.exec(rgb);
    if (!m || (m[4] !== undefined && Number(m[4]) === 0)) return -1;
    return (0.2126 * m[1] + 0.7152 * m[2] + 0.0722 * m[3]) / 255;
  };
  const isLight = (el) => {
    const said = el.dataset.snowfallOn;
    if (said === 'light' || said === 'dark') return said === 'light';
    for (let node = el; node && node !== document.documentElement; node = node.parentElement) {
      const l = luminance(getComputedStyle(node).backgroundColor);
      if (l >= 0) return l > 0.5;
    }
    return true;
  };

  /* ---------- 1. snowfall, one field per band ---------- */
  const FRAME_MS = 1000 / 30 - 2; // a little under the interval, so a 30fps tick is never skipped
  // Every band is measured first (its colour and its size), then every canvas
  // goes in: reads before writes, so the browser lays the page out once for
  // all the snow rather than once per band.
  const measure = (host) => {
    const layer = host.querySelector('[data-snowfall-layer]') || host;
    return { host, layer, light: isLight(host), w: layer.clientWidth, h: layer.clientHeight };
  };
  class Field {
    constructor(m) {
      this.host = m.host;
      this.layer = m.layer;
      this.canvas = document.createElement('canvas');
      this.canvas.className = 'snowfall';
      this.canvas.setAttribute('aria-hidden', 'true');
      if (this.layer === this.host) this.host.prepend(this.canvas);
      else this.layer.append(this.canvas);
      this.ctx = this.canvas.getContext('2d');
      this.flakes = [];
      this.dots = [];
      this.w = 0;
      this.h = 0;
      this.raf = 0;
      this.last = 0;
      this.on = false;
      this.light = m.light;
      this.host.classList.add('has-snowfall');
      this.tick = this.tick.bind(this);
      this.resize(m.w, m.h);
    }
    // Sizes arrive from the measurement or the ResizeObserver; the band is
    // only measured here when neither is given.
    resize(cw, ch, recheck) {
      const w = Math.max(1, Math.round(cw === undefined ? this.layer.clientWidth : cw));
      const h = Math.max(1, Math.round(ch === undefined ? this.layer.clientHeight : ch));
      if (w === this.w && h === this.h) return;
      if (recheck) this.light = isLight(this.host);
      const first = !this.w;
      const sx = first ? 1 : w / this.w;
      const sy = first ? 1 : h / this.h;
      this.w = w;
      this.h = h;
      this.canvas.width = Math.round(w * dpr);
      this.canvas.height = Math.round(h * dpr);
      const area = w * h;
      // A band can ask for lighter snow, e.g. data-snowfall="0.4" behind the hero's tree.
      // Phones get seven tenths of the flakes: the same gentle fall, less work per frame on a slow CPU.
      const k = Math.min(1, Math.max(0.1, Number(this.host.dataset.snowfall) || 1)) * (SMALL ? 0.7 : 1);
      const nFlakes = Math.round(Math.min(56, Math.max(8, area / 26000)) * k);
      const nDots = Math.round(Math.min(110, Math.max(16, area / 11000)) * k);
      if (first) {
        this.flakes = Array.from({ length: nFlakes }, () => this.flake(true));
        this.dots = Array.from({ length: nDots }, () => this.dot(true));
      } else {
        this.flakes.forEach((f) => {
          f.x *= sx;
          f.y *= sy;
        });
        this.dots.forEach((d) => {
          d.x *= sx;
          d.y *= sy;
        });
        while (this.flakes.length < nFlakes) this.flakes.push(this.flake(true));
        while (this.dots.length < nDots) this.dots.push(this.dot(true));
        this.flakes.length = nFlakes;
        this.dots.length = nDots;
      }
      if (!this.on) this.draw(0, 0);
    }
    flake(anywhere) {
      const depth = Math.random();
      const size = 9 + depth * 15;
      const set = this.light ? (Math.random() < 0.3 ? SPARKLE : SNOW_RIMMED) : Math.random() < 0.22 ? CHAMPAGNE : SNOW;
      return {
        x: Math.random() * this.w,
        y: anywhere ? Math.random() * this.h : -size,
        s: size,
        img: set[(Math.random() * 4) | 0],
        rot: Math.random() * TAU,
        vr: (Math.random() - 0.5) * 0.7,
        vy: 12 + depth * 24,
        sway: 6 + Math.random() * 14,
        swf: 0.3 + Math.random() * 0.6,
        ph: Math.random() * TAU,
        // rimmed flakes are quieter by nature, so they start a little stronger
        a: (this.light ? 0.4 : 0.28) + depth * 0.5,
      };
    }
    // Snow dust: Soft Snow on the taupe band, taupe-light on the light ones, a few champagne.
    dot(anywhere) {
      return {
        x: Math.random() * this.w,
        y: anywhere ? Math.random() * this.h : -4,
        r: 0.6 + Math.random() * 1.1,
        vy: 8 + Math.random() * 16,
        ph: Math.random() * TAU,
        a: (0.18 + Math.random() * 0.45) * (this.light ? 0.75 : 1),
        gold: Math.random() < 0.15,
      };
    }
    draw(t, dt) {
      const ctx = this.ctx;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, this.w, this.h);
      const dust = this.light ? '#B3A08E' : '#FAF8F3';
      for (let i = 0; i < this.dots.length; i++) {
        const d = this.dots[i];
        d.y += d.vy * dt;
        if (d.y > this.h + 4) this.dots[i] = this.dot(false);
        ctx.globalAlpha = d.a;
        ctx.fillStyle = d.gold ? '#C3A26E' : dust;
        ctx.fillRect(d.x + Math.sin(t * 0.6 + d.ph) * 4 - d.r, d.y - d.r, d.r * 2, d.r * 2);
      }
      for (let i = 0; i < this.flakes.length; i++) {
        let f = this.flakes[i];
        f.y += f.vy * dt;
        f.rot += f.vr * dt;
        if (f.y - f.s > this.h) f = this.flakes[i] = this.flake(false);
        const x = f.x + Math.sin(t * f.swf + f.ph) * f.sway;
        const c = Math.cos(f.rot);
        const s = Math.sin(f.rot);
        ctx.setTransform(c * dpr, s * dpr, -s * dpr, c * dpr, x * dpr, f.y * dpr);
        ctx.globalAlpha = f.a;
        ctx.drawImage(f.img, -f.s / 2, -f.s / 2, f.s, f.s);
      }
      ctx.globalAlpha = 1;
    }
    tick(now) {
      if (!this.on) return;
      this.raf = requestAnimationFrame(this.tick);
      // Drifting snow reads the same at 30 frames a second, for half the drawing.
      if (this.last && now - this.last < FRAME_MS) return;
      const dt = this.last ? Math.min(0.08, (now - this.last) / 1000) : 1 / 30;
      this.last = now;
      this.draw(now / 1000, dt);
    }
    start() {
      if (this.on) return;
      this.on = true;
      this.last = 0;
      this.raf = requestAnimationFrame(this.tick);
    }
    stop() {
      this.on = false;
      cancelAnimationFrame(this.raf);
    }
  }

  // The fields are built once the page has loaded (and the browser is idle),
  // so the first picture and the fonts are not competing with the snow.
  const startFields = () => {
    sprites();
    const measured = hosts.map(measure);
    const fields = new Map();
    measured.forEach((m) => fields.set(m.host, new Field(m)));

    const visible = new Set();
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          const f = fields.get(e.target);
          if (!f) return;
          if (e.isIntersecting) {
            visible.add(f);
            if (!document.hidden) f.start();
          } else {
            visible.delete(f);
            f.stop();
          }
        }),
      { rootMargin: '80px 0px' }
    );
    fields.forEach((_, host) => io.observe(host));

    if ('ResizeObserver' in window) {
      const byLayer = new Map();
      fields.forEach((f) => byLayer.set(f.layer, f));
      const ro = new ResizeObserver((entries) =>
        entries.forEach((e) => {
          const f = byLayer.get(e.target);
          if (f) f.resize(e.contentRect.width, e.contentRect.height, true);
        })
      );
      byLayer.forEach((_, layer) => ro.observe(layer));
    }

    document.addEventListener('visibilitychange', () => {
      visible.forEach((f) => (document.hidden ? f.stop() : f.start()));
    });
  };
  if (hosts.length && hasIO) {
    const whenIdle = () => {
      if ('requestIdleCallback' in window) window.requestIdleCallback(startFields, { timeout: 1500 });
      else setTimeout(startFields, 200);
    };
    if (document.readyState === 'complete') whenIdle();
    else window.addEventListener('load', whenIdle, { once: true });
  }

  /* ---------- 2. snow from the pointer ---------- */
  if (!(theme.motion && theme.motion.cursorSnow) || theme.designMode) return;

  const LIGHT = '.scheme-paper, .scheme-cream, .scheme-cashmere, .scheme-gold, .mega__panel, .search-modal';
  const MAX = 110;
  const flakes = [];
  let canvas = null;
  let ctx = null;
  let W = 0;
  let H = 0;
  let raf = 0;
  let last = 0;
  let lastX = null;
  let lastY = null;
  let travelled = 0;
  let paperAt = { t: -1, x: 0, y: 0, light: false };

  const size = () => {
    W = window.innerWidth;
    H = window.innerHeight;
    canvas.width = Math.round(W * dpr);
    canvas.height = Math.round(H * dpr);
  };
  const ensure = () => {
    if (canvas) return;
    canvas = document.createElement('canvas');
    canvas.className = 'snow-cursor';
    canvas.setAttribute('aria-hidden', 'true');
    document.body.append(canvas);
    ctx = canvas.getContext('2d');
    size();
    window.addEventListener('resize', size, { passive: true });
  };
  // Is the pointer over a light band? Looked up at most every 150ms.
  const onPaper = (x, y, now) => {
    if (now - paperAt.t < 150 && Math.abs(x - paperAt.x) < 60 && Math.abs(y - paperAt.y) < 60) return paperAt.light;
    const el = document.elementFromPoint(x, y);
    const light = !!(el && el.closest && el.closest(LIGHT));
    paperAt = { t: now, x, y, light };
    return light;
  };
  const spawn = (x, y, light, burst = 0) => {
    sprites();
    if (flakes.length >= MAX) flakes.shift();
    const depth = Math.random();
    const set = light ? (Math.random() < 0.35 ? SPARKLE : SNOW_RIMMED) : Math.random() < 0.2 ? CHAMPAGNE : SNOW;
    const a = Math.random() * TAU;
    const speed = burst ? burst * (0.45 + Math.random() * 0.55) : 0;
    flakes.push({
      x: x + (Math.random() - 0.5) * 10,
      y: y + (Math.random() - 0.5) * 10,
      vx: Math.cos(a) * speed + (Math.random() - 0.5) * 18,
      vy: Math.sin(a) * speed * 0.6 - (burst ? 30 : 0) + 8 + Math.random() * 14,
      s: 8 + depth * 10,
      img: set[(Math.random() * 4) | 0],
      rot: Math.random() * TAU,
      vr: (Math.random() - 0.5) * 2.2,
      sway: 5 + Math.random() * 12,
      swf: 1.1 + Math.random() * 1.4,
      ph: Math.random() * TAU,
      age: 0,
      life: 1.5 + Math.random() * 1.3,
      a: light ? 0.9 : 0.7 + depth * 0.3,
    });
    if (!raf) {
      last = 0;
      raf = requestAnimationFrame(frame);
    }
  };
  function frame(now) {
    const dt = last ? Math.min(0.05, (now - last) / 1000) : 1 / 60;
    last = now;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    for (let i = flakes.length - 1; i >= 0; i--) {
      const f = flakes[i];
      f.age += dt;
      if (f.age >= f.life) {
        flakes.splice(i, 1);
        continue;
      }
      f.vy = Math.min(70, f.vy + 46 * dt); // a gentle pull down, then a steady drift
      f.vx *= 1 - 1.6 * dt;
      f.x += f.vx * dt;
      f.y += f.vy * dt;
      f.rot += f.vr * dt;
      const p = f.age / f.life;
      const fade = Math.min(1, f.age / 0.12) * (p > 0.55 ? 1 - (p - 0.55) / 0.45 : 1);
      const x = f.x + Math.sin(f.age * f.swf * TAU * 0.5 + f.ph) * f.sway;
      const c = Math.cos(f.rot);
      const s = Math.sin(f.rot);
      ctx.setTransform(c * dpr, s * dpr, -s * dpr, c * dpr, x * dpr, f.y * dpr);
      ctx.globalAlpha = f.a * fade;
      ctx.drawImage(f.img, -f.s / 2, -f.s / 2, f.s, f.s);
    }
    ctx.globalAlpha = 1;
    if (flakes.length) {
      raf = requestAnimationFrame(frame);
    } else {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      raf = 0;
    }
  }

  const locked = () => document.documentElement.classList.contains('is-locked') || document.documentElement.classList.contains('intro-active');
  window.addEventListener(
    'pointermove',
    (e) => {
      if (e.pointerType === 'touch' || locked()) return;
      const x = e.clientX;
      const y = e.clientY;
      if (lastX !== null) travelled += Math.hypot(x - lastX, y - lastY);
      lastX = x;
      lastY = y;
      if (travelled < 16) return;
      const n = Math.min(2, Math.floor(travelled / 16));
      travelled = 0;
      ensure();
      const light = onPaper(x, y, e.timeStamp);
      for (let k = 0; k < n; k++) spawn(x, y, light);
    },
    { passive: true }
  );
  // A tap on a phone lets go a small flurry where the finger was. Only a tap:
  // a finger that moves is scrolling, and snow then would cost the scroll.
  let downX = 0;
  let downY = 0;
  let downAt = 0;
  window.addEventListener(
    'pointerdown',
    (e) => {
      if (e.pointerType !== 'touch') return;
      downX = e.clientX;
      downY = e.clientY;
      downAt = e.timeStamp;
    },
    { passive: true }
  );
  window.addEventListener(
    'pointerup',
    (e) => {
      if (e.pointerType !== 'touch' || locked()) return;
      if (e.timeStamp - downAt > 500 || Math.hypot(e.clientX - downX, e.clientY - downY) > 10) return;
      ensure();
      const light = onPaper(e.clientX, e.clientY, e.timeStamp);
      for (let k = 0; k < 9; k++) spawn(e.clientX, e.clientY, light, 70);
    },
    { passive: true }
  );
})();
